import React, {
    useState,
    useRef,
    useEffect,
    forwardRef,
    useCallback,
} from "react";
import "./TextField.module.css";

/*
  Custom TextField component meant to be a near drop-in replacement for MUI's TextField.
  This version specifically fixes integration with MUI Autocomplete by:
  - honoring params.InputProps.ref (root ref for Popper anchor)
  - composing (not overriding) params.inputProps event handlers
  - honoring InputLabelProps.shrink to force a floating label when needed
*/

const callAll =
    (...fns) =>
    (event) => {
        for (const fn of fns) if (typeof fn === "function") fn(event);
    };

const TextField = forwardRef(function TextField(props, ref) {
    const {
        id,
        name,
        label,
        variant = "outlined",
        size = "medium",
        fullWidth = false,
        disabled = false,
        readOnly = false,
        required = false,
        error = false,
        helperText,
        placeholder,
        value,
        defaultValue,
        onChange,
        onFocus,
        onBlur,
        autoFocus = false,
        type = "text",
        multiline = false,
        rows,
        maxRows,
        select = false,
        selectOptions,
        children,
        InputProps = {}, // may include: startAdornment, endAdornment, ref, ... (from MUI Autocomplete)
        InputLabelProps = {}, // may include: shrink (from MUI Autocomplete)
        inputProps = {}, // HTML input props (from MUI Autocomplete)
        startAdornment = null,
        endAdornment = null,
        className = "",
        style = {},
        showPasswordToggle = true,
        showCharCount = false,
        maxLength,
        autoSize = false,
        ...rest
    } = props;

    // Compose adornments from InputProps (MUI) and explicit props
    const startNode = InputProps.startAdornment ?? startAdornment;
    const endNode = InputProps.endAdornment ?? endAdornment;

    // Make endAdornment clickable if it's a button/icon
    const handleEndAdornmentClick = (e) => {
        if (endNode && endNode.props && endNode.props.onClick) {
            endNode.props.onClick(e);
        }
    };
    // Extract root-level props from InputProps (used by MUI Autocomplete)
    const {
        startAdornment: _ignoredStart,
        endAdornment: _ignoredEnd,
        ref: _ignoredRef,
        className: inputRootClassName,
        ...rootInputProps
    } = InputProps || {};

    // Controlled vs uncontrolled
    const isControlled = value !== undefined;
    const [internalValue, setInternalValue] = useState(defaultValue ?? "");
    const currentValue = isControlled ? value : internalValue;
    const displayValue =
        inputProps && Object.prototype.hasOwnProperty.call(inputProps, "value")
            ? inputProps.value
            : currentValue;

    // Refs
    const inputRef = useRef(null);
    const rootRef = useRef(null);

    // compose forwarded ref -> input element
    useEffect(() => {
        if (!ref) return;
        if (typeof ref === "function") ref(inputRef.current);
        else ref.current = inputRef.current;
    }, [ref]);

    // expose root ref to external (Autocomplete uses this for Popper anchor)
    const setRootRef = useCallback(
        (node) => {
            rootRef.current = node;
            const ext = InputProps && InputProps.ref;
            if (ext) {
                if (typeof ext === "function") ext(node);
                else if (typeof ext === "object") ext.current = node;
            }
        },
        [InputProps]
    );

    const [focused, setFocused] = useState(false);
    const [showPwd, setShowPwd] = useState(false);

    // auto-size textarea
    useEffect(() => {
        if (!autoSize || !multiline) return;
        const el = inputRef.current;
        if (!el) return;
        el.style.height = "auto";
        const max = maxRows ? maxRows * 24 : null; // rough line height
        const newH = el.scrollHeight;
        if (max && newH > max) el.style.height = `${max}px`;
        else el.style.height = `${newH}px`;
    }, [currentValue, autoSize, multiline, maxRows]);

    useEffect(() => {
        if (autoFocus && inputRef.current) inputRef.current.focus();
    }, [autoFocus]);

    // Compose handlers so we do NOT clobber Autocomplete's ones
    const onInputChange = callAll(inputProps.onChange, (e) => {
        if (!isControlled) setInternalValue(e.target.value);
        if (onChange) onChange(e);
    });

    const onInputFocus = callAll(inputProps.onFocus, (e) => {
        setFocused(true);
        if (onFocus) onFocus(e);
    });

    const onInputBlur = callAll(inputProps.onBlur, (e) => {
        setFocused(false);
        if (onBlur) onBlur(e);
    });

    // Float label if focused / has value / placeholder visible / Autocomplete asked to shrink
    const shrink = Boolean(InputLabelProps && InputLabelProps.shrink);
    const showFloating = Boolean(
        label &&
            (shrink ||
                focused ||
                (displayValue !== undefined && displayValue !== "") ||
                placeholder)
    );

    const computedId =
        id || name || `ctf-${Math.random().toString(36).slice(2, 9)}`;
    const inputId =
        (props && props.id) || (inputProps && inputProps.id) || computedId;
    const helperId = helperText ? `${inputId}-helper` : undefined;

    const rootClass = [
        "ctf-root",
        variant,
        focused ? "focused" : "",
        error ? "error" : "",
        disabled ? "disabled" : "",
        inputRootClassName || "",
    ]
        .filter(Boolean)
        .join(" ");

    const containerClass = [
        "ctf-container",
        fullWidth ? "ctf-fullWidth" : "",
        size === "small" ? "ctf-small" : "",
        className,
    ]
        .filter(Boolean)
        .join(" ");

    // Common props for native input/textarea
    const inputCommonProps = {
        id: inputId,
        name,
        ref: inputRef,
        disabled,
        readOnly,
        required,
        placeholder: placeholder && !label ? placeholder : undefined,
        "aria-invalid": !!error,
        "aria-describedby": helperId,
        // Merge all html input props coming from Autocomplete etc
        ...inputProps,
        ...(!isControlled ? {} : { value }),
        onChange: onInputChange,
        onFocus: onInputFocus,
        onBlur: onInputBlur,
    };

    // Select rendering (native). Not used by MUI Autocomplete, but supported.
    const renderSelect = () => (
        <div className="ctf-select-wrapper">
            <select
                {...inputCommonProps}
                className="ctf-input"
                onChange={callAll(inputProps.onChange, handleSelectChange)}
            >
                {children
                    ? children
                    : (selectOptions || []).map((opt) =>
                          typeof opt === "object" ? (
                              <option key={opt.value} value={opt.value}>
                                  {opt.label}
                              </option>
                          ) : (
                              <option key={opt} value={opt}>
                                  {opt}
                              </option>
                          )
                      )}
            </select>
            <span className="ctf-select-arrow" aria-hidden>
                ▼
            </span>
        </div>
    );

    const handleSelectChange = (e) => {
        if (!isControlled) setInternalValue(e.target.value);
        if (onChange) onChange(e);
    };

    const effectiveType =
        type === "password" && showPasswordToggle
            ? showPwd
                ? "text"
                : "password"
            : type;

    const renderInput = () => {
        if (select) return renderSelect();

        if (multiline) {
            return (
                <textarea
                    rows={rows}
                    maxLength={maxLength}
                    {...inputCommonProps}
                    className="ctf-textarea"
                />
            );
        }

        const onInputKeyDown = callAll(
            rootInputProps.onKeyDown,
            inputProps.onKeyDown
        );
        const onInputMouseDown = callAll(
            rootInputProps.onMouseDown,
            inputProps.onMouseDown
        );

        return (
            <input
                ref={inputProps && inputProps.ref ? inputProps.ref : inputRef}
                id={inputId}
                name={name}
                type={type}
                className="ctf-input"
                disabled={disabled}
                placeholder={label && !shrink ? "" : placeholder}
                value={
                    props.inputProps &&
                    Object.prototype.hasOwnProperty.call(
                        props.inputProps,
                        "value"
                    )
                        ? props.inputProps.value
                        : props.value !== undefined
                        ? props.value
                        : internalValue
                }
                onChange={(e) => {
                    props.inputProps?.onChange?.(e); // Autocomplete’s handler
                    onChange?.(e); // user’s handler
                    if (
                        props.value === undefined &&
                        !(
                            props.inputProps &&
                            Object.prototype.hasOwnProperty.call(
                                props.inputProps,
                                "value"
                            )
                        )
                    ) {
                        setInternalValue(e.target.value);
                    }
                }}
                onFocus={(e) => {
                    props.inputProps?.onFocus?.(e);
                    onFocus?.(e);
                    setFocused(true);
                }}
                onBlur={(e) => {
                    props.inputProps?.onBlur?.(e);
                    onBlur?.(e);
                    setFocused(false);
                }}
                onKeyDown={onInputKeyDown}
                onMouseDown={onInputMouseDown}
                {...props.inputProps}
                {...rootInputProps}
            />
        );
    };

    const charCountValue = displayValue ? String(displayValue).length : 0;

    return (
        <div className={containerClass} style={style} {...rest}>
            <div className={rootClass} ref={setRootRef} {...rootInputProps}>
                {startNode && (
                    <div className="ctf-adornment ctf-start">
                        {startNode}
                    </div>
                )}

                {renderInput()}

                {(type === "password" && showPasswordToggle) || endNode ? (
                    <div className="ctf-adornment ctf-end">
                        {endNode}
                        {type === "password" && showPasswordToggle && (
                            <button
                                type="button"
                                onClick={() => setShowPwd((s) => !s)}
                                aria-label={
                                    showPwd ? "Hide password" : "Show password"
                                }
                                style={{
                                    background: "transparent",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: 6,
                                }}
                                disabled={disabled}
                            >
                                {showPwd ? "Hide" : "Show"}
                            </button>
                        )}
                    </div>
                ) : null}

                {label && (
                    <label
                        htmlFor={inputId}
                        className={`ctf-label ${
                            showFloating ? "floating" : ""
                        } ${error ? "error" : ""} ${
                            disabled ? "disabled" : ""
                        }`}
                    >
                        {label}
                        {required ? (
                            <span aria-hidden style={{ color: "#d32f2f" }}>
                                {" "}
                                *
                            </span>
                        ) : null}
                    </label>
                )}
            </div>

            {(helperText || showCharCount) && (
                <div className={`ctf-helper ${error ? "error" : ""}`}>
                    {helperText || "\u00A0"}
                    {showCharCount ? (
                        <div className="ctf-charcount">
                            {charCountValue}
                            {maxLength ? ` / ${maxLength}` : ""}
                        </div>
                    ) : null}
                </div>
            )}
        </div>
    );
});

export default TextField;
